import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Reveal } from '@/components/motion';
import { CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { public_api } from '@/lib/api';
import { SERVICES } from '@/data/site';

const REFERRER_TYPES = [
  { v: 'support_coordinator', l: 'Support Coordinator' },
  { v: 'participant', l: 'Participant (myself)' },
  { v: 'family', l: 'Family / guardian' },
  { v: 'provider', l: 'Health / aged care provider' },
  { v: 'other', l: 'Other' },
];

export default function Referral() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    referrer_type: 'support_coordinator',
    referrer_name: '', referrer_email: '', referrer_phone: '', referrer_organisation: '',
    participant_name: '', participant_ndis_number: '', participant_contact: '',
    services_needed: [], urgency: 'standard', notes: '',
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleService = (s) => setForm(f => ({
    ...f,
    services_needed: f.services_needed.includes(s)
      ? f.services_needed.filter(x => x !== s)
      : [...f.services_needed, s]
  }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await public_api.referral(form);
      setDone(true);
      toast.success('Referral received. Our onboarding team will be in touch.');
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <section className="pt-16 md:pt-24 pb-32" data-testid="referral-success">
        <div className="container-x max-w-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-8">
            <CheckCircle size={40} weight="fill" className="text-brand" />
          </div>
          <h1 className="h-section text-balance">Referral received.</h1>
          <p className="body-lg mt-6 text-pretty">
            Thank you {form.referrer_name}. Our onboarding team will contact you within one business day to progress the referral.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="btn-primary">Back to home</Link>
            <Link to="/services" className="btn-secondary">Explore services</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-16 md:pt-24 pb-24" data-testid="referral-page">
      <div className="container-x max-w-3xl">
        <Reveal>
          <div className="overline mb-6">Referrals</div>
          <h1 className="h-section text-balance">Refer a participant to Inclusive Mobility Group.</h1>
          <p className="body-md mt-4 text-pretty max-w-2xl">
            Support coordinators, families and providers — this form onboards a participant quickly. All information is
            kept confidential and used only to establish services.
          </p>
        </Reveal>
        <form onSubmit={submit} className="mt-10 p-8 md:p-10 rounded-3xl bg-neutral-50 border border-neutral-200 space-y-6" data-testid="referral-form">
          <div>
            <label className="label-base">I am the…</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {REFERRER_TYPES.map(rt => (
                <button
                  key={rt.v}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, referrer_type: rt.v }))}
                  data-testid={`referral-type-${rt.v}`}
                  className={`p-3 rounded-xl border text-sm font-medium transition ${
                    form.referrer_type === rt.v
                      ? 'border-brand bg-brand text-white'
                      : 'border-neutral-300 bg-white text-ink hover:border-brand'
                  }`}
                >
                  {rt.l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-base">Your name</label>
              <input required value={form.referrer_name} onChange={set('referrer_name')} data-testid="referral-name" className="input-base" />
            </div>
            <div>
              <label className="label-base">Organisation (if applicable)</label>
              <input value={form.referrer_organisation} onChange={set('referrer_organisation')} data-testid="referral-org" className="input-base" />
            </div>
            <div>
              <label className="label-base">Your email</label>
              <input required type="email" value={form.referrer_email} onChange={set('referrer_email')} data-testid="referral-email" className="input-base" />
            </div>
            <div>
              <label className="label-base">Your phone</label>
              <input required value={form.referrer_phone} onChange={set('referrer_phone')} data-testid="referral-phone" className="input-base" />
            </div>
          </div>
          <div className="pt-4 border-t border-neutral-200 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="label-base">Participant name</label>
                <input required value={form.participant_name} onChange={set('participant_name')} data-testid="referral-participant-name" className="input-base" />
              </div>
              <div>
                <label className="label-base">Participant NDIS #</label>
                <input value={form.participant_ndis_number} onChange={set('participant_ndis_number')} data-testid="referral-ndis" className="input-base" />
              </div>
            </div>
            <div>
              <label className="label-base">Best contact for participant</label>
              <input value={form.participant_contact} onChange={set('participant_contact')} data-testid="referral-participant-contact" className="input-base" placeholder="Phone or email — whichever is best" />
            </div>
          </div>
          <div>
            <label className="label-base">Services needed (select all that apply)</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SERVICES.map(s => {
                const active = form.services_needed.includes(s.name);
                return (
                  <button
                    key={s.slug} type="button"
                    data-testid={`referral-service-${s.slug}`}
                    onClick={() => toggleService(s.name)}
                    className={`p-3 rounded-xl border text-sm font-medium text-left transition ${
                      active
                        ? 'border-brand bg-brand text-white'
                        : 'border-neutral-300 bg-white text-ink hover:border-brand'
                    }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-base">Urgency</label>
              <select value={form.urgency} onChange={set('urgency')} data-testid="referral-urgency" className="input-base">
                <option value="standard">Standard (within a week)</option>
                <option value="priority">Priority (within 48 hours)</option>
                <option value="urgent">Urgent (today)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-base">Notes</label>
            <textarea rows={4} value={form.notes} onChange={set('notes')} data-testid="referral-notes" className="input-base resize-none" placeholder="Context, preferences, funding notes, communication requirements, etc." />
          </div>
          <button type="submit" disabled={busy} data-testid="referral-submit" className="btn-primary w-full md:w-auto">
            {busy ? 'Sending…' : 'Submit referral'} <ArrowRight size={16} weight="bold" />
          </button>
        </form>
      </div>
    </section>
  );
}
